// Prop catalogue (Feature P1): what hitting each prop kind costs, and how the
// invoice says it. Pure data plus two lookups; the sim reads `damageFor`.
//
//   minor = OBSTACLE_PENALTY_AUD (A$15, the long-standing obstacle hit)
//   major = MAJOR_HIT_PENALTY_AUD (A$50, the same as a do-not-mow violation)
//   none  = soft edge: blocks the mower, never bills
//
// Pitch, wicket square and bowls green are the other "major" damage, but they
// are zones, not props: they bill through VIOLATION_PENALTY_AUD as before.

import type { DamageOnHit, Prop, PropKind, ZoneId } from './types';
import { OBSTACLE_PENALTY_AUD, VIOLATION_PENALTY_AUD } from './contract';

/** A major prop hit costs what a do-not-mow violation costs (DECISIONS.md, Feature pass). */
export const MAJOR_HIT_PENALTY_AUD = VIOLATION_PENALTY_AUD;

/**
 * Damage per prop kind. Everything that billed before stays `minor` except
 * the vehicles and the pool. `none` only for kinds that are soft edges (the
 * flower-bed edging, hedges) or were never billed (the house roof).
 */
export const PROP_DAMAGE: Record<PropKind, DamageOnHit> = {
  'hills-hoist': 'minor', trampoline: 'minor', 'veggie-bed': 'minor', shed: 'minor', 'dog-bowl': 'minor',
  cubby: 'minor', letterbox: 'minor', 'wheelie-bin': 'minor', 'gum-tree': 'minor', 'colorbond-fence': 'minor',
  'goal-post': 'minor', 'boundary-fence': 'minor', bench: 'minor', canteen: 'minor', scoreboard: 'minor',
  'sight-screen': 'minor', covers: 'minor', rope: 'minor', playground: 'minor', 'shade-sail': 'minor',
  sandpit: 'minor', verandah: 'minor', annexe: 'minor', car: 'major', 'camp-kitchen': 'minor',
  headstone: 'minor', 'water-tank': 'minor', sheep: 'minor', pool: 'major', sprinkler: 'minor',
  'flower-bed': 'none', tap: 'minor', 'power-pit': 'minor', rock: 'minor', ute: 'major', caravan: 'minor',
  house: 'none', 'picket-fence': 'minor', 'brick-fence': 'minor', hedge: 'none',
  // Feature P9: low things you roll over; they never block, so never bill.
  'garden-hose': 'none', 'brick-edging': 'none', 'stone-edging': 'none',
};

/**
 * Feature P6: per-kind traits. `debrisSurface` marks a deck or step footprint
 * (the verandah's deck, a caravan annexe's floor): leaves resting on it count
 * against a clear, like paved and bed cells. Patio slabs are paved (`p`)
 * cells already. Only a non-solid footprint can hold leaves.
 */
export const PROP_TRAITS: Partial<Record<PropKind, { debrisSurface?: true; passable?: true; scatter?: true }>> = {
  verandah: { debrisSurface: true },
  annexe: { debrisSurface: true },
  // Feature P9: drivable low items. Loose stone edging also scatters.
  'garden-hose': { passable: true },
  'brick-edging': { passable: true },
  'stone-edging': { passable: true, scatter: true },
};

/** Feature P9: a rock at most this across (m) is passable; bigger rocks stay solid. */
export const SMALL_ROCK_MAX_M = 0.7;

/**
 * Feature P9 (SimOptions.passableProps): does the mower roll over it? The
 * prop's own flag wins, then the kind's trait, then size for rocks.
 */
export function isPassable(p: Pick<Prop, 'kind' | 'passable' | 'r' | 'w' | 'h'>): boolean {
  if (p.passable !== undefined) return p.passable;
  if (PROP_TRAITS[p.kind]?.passable) return true;
  if (p.kind !== 'rock') return false;
  const across = p.r !== undefined ? 2 * p.r : Math.max(p.w ?? 0.5, p.h ?? 0.5);
  return across <= SMALL_ROCK_MAX_M + 1e-9;
}

/** Feature P9: passable and throws loose stone/mulch about when driven over. */
export function scattersOnPass(p: Pick<Prop, 'kind' | 'passable' | 'scatter' | 'r' | 'w' | 'h'>): boolean {
  return isPassable(p) && (p.scatter ?? !!PROP_TRAITS[p.kind]?.scatter);
}

/**
 * Feature P9: the three obstacle states with passable props on. `solid` stops
 * the mower (and bills per the damage table), `passable` is a bump, `scatter`
 * a bump that throws stones; `none` is decoration the mower never meets.
 */
export type ObstacleState = 'solid' | 'passable' | 'scatter' | 'none';

export function obstacleState(p: Pick<Prop, 'kind' | 'solid' | 'passable' | 'scatter' | 'r' | 'w' | 'h'>): ObstacleState {
  if (isPassable(p)) return scattersOnPass(p) ? 'scatter' : 'passable';
  return p.solid ? 'solid' : 'none';
}

/** Does this prop's footprint count for debris? The prop's own flag wins over its kind's. */
export function isDebrisSurface(p: Pick<Prop, 'kind' | 'debrisSurface'>): boolean {
  return p.debrisSurface ?? !!PROP_TRAITS[p.kind]?.debrisSurface;
}

/** A$ for one hit of this damage. */
export function hitPenaltyAud(damage: DamageOnHit): number {
  return damage === 'major' ? MAJOR_HIT_PENALTY_AUD : damage === 'minor' ? OBSTACLE_PENALTY_AUD : 0;
}

/**
 * What hitting this prop costs: the prop's own `damageOnHit`, else `none`
 * when the layout marked it `penalty: false` (the original opt-out), else the
 * kind's catalogue entry.
 */
export function damageFor(p: Pick<Prop, 'kind' | 'penalty' | 'damageOnHit'>): DamageOnHit {
  if (p.damageOnHit) return p.damageOnHit;
  if (p.penalty === false) return 'none';
  return PROP_DAMAGE[p.kind] ?? 'minor';
}

/** Short name for copy ("Bit rough on the fence, mate"). */
const PROP_SHORT: Partial<Record<PropKind, string>> = {
  'colorbond-fence': 'fence', 'boundary-fence': 'fence', 'picket-fence': 'fence', 'brick-fence': 'fence',
  'hills-hoist': 'Hills hoist', 'gum-tree': 'gum', 'wheelie-bin': 'bins', 'goal-post': 'goal posts',
  'veggie-bed': 'veggie bed', 'water-tank': 'water tank', 'sight-screen': 'sight screen',
  'camp-kitchen': 'camp kitchen', 'power-pit': 'power pit', 'dog-bowl': 'dog bowl', 'shade-sail': 'shade sail',
};

export function propShortName(kind: PropKind): string {
  return PROP_SHORT[kind] ?? kind.replace(/-/g, ' ');
}

/** One invoice reason per prop kind, as in "3 × clipped the fence". */
const HIT_REASON: Partial<Record<PropKind, string>> = {
  'colorbond-fence': 'clipped the fence', 'boundary-fence': 'clipped the fence',
  'picket-fence': 'clipped the fence', 'brick-fence': 'clipped the fence',
  letterbox: 'bumped the letterbox', 'wheelie-bin': 'skittled a wheelie bin', 'hills-hoist': 'hit the Hills hoist',
  'goal-post': 'clipped a goal post', 'gum-tree': 'barked the gum', rock: 'scraped a rock',
  'veggie-bed': 'ran into the veggie bed', trampoline: 'hit the trampoline', shed: 'bumped the shed',
  'water-tank': 'dented the water tank', sprinkler: 'clipped a sprinkler', headstone: 'clipped a headstone',
  sheep: 'bumped a sheep', bench: 'hit the bench', tap: 'knocked the tap', car: 'dinged the car',
  ute: 'dinged the ute', pool: 'nicked the pool', caravan: 'scraped the caravan', 'dog-bowl': 'flattened the dog bowl',
};

export function hitReason(kind: PropKind): string {
  return HIT_REASON[kind] ?? `hit the ${kind.replace(/-/g, ' ')}`;
}

/** Invoice reason for a do-not-mow violation in this zone. */
export function violationReason(zone: ZoneId | undefined): string {
  if (zone === 'pitch' || zone === 'wicket-square') return 'ran over the wicket';
  if (zone === 'green') return 'scalped the bowls green';
  return 'mowed the do-not-mow';
}
